import { type Ref, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  changedKeys,
  computeInitialFilter,
  emptyFilter,
  parseAll,
  queryEqual,
  serializeAll,
  shallowEqualValue,
  snapshot,
} from './query'
import type { QueryFilterSchema, UseQueryFilterOptions } from './types'

/**
 * Bidirectional sync between a typed filter object and `route.query`.
 *
 * - Hydrates synchronously from the current URL during `setup` (no async gate needed).
 * - State → URL: serializes via schema, omits default values, calls `router.replace` (default) or `router.push`.
 * - URL → state: re-parses on `route.query` changes (back/forward), patches only changed keys.
 * - `resetPageOn`: any listed field changing (from the user, not the URL) resets `page` to 1.
 *
 * Pass `defaults` so the parser can fall back per-field and the serializer can omit defaults
 * from the URL. The returned `filter` is a `Ref<TFilter>` — mutate via `filter.value.x = ...`
 * or replace via `filter.value = {...}`.
 */
export const useQueryFilter = <TFilter extends Record<string, unknown>>(
  schema: QueryFilterSchema<TFilter>,
  defaults: TFilter,
  options: UseQueryFilterOptions<TFilter> = {},
): { filter: Ref<TFilter>; reset: () => void } => {
  const route = useRoute()
  const router = useRouter()
  const mode = options.mode ?? 'replace'
  const resetPageOn = new Set((options.resetPageOn ?? []).map(String))
  const hasPageField = 'page' in schema

  const initial = computeInitialFilter(schema, route.query, defaults)
  const filter = ref(initial) as Ref<TFilter>

  let isSyncingFromQuery = false
  let previous = snapshot(initial)

  const initialQuery = serializeAll(schema, initial, route.query)
  if (!queryEqual(initialQuery, route.query)) {
    void router.replace({ query: initialQuery })
  }

  const writeToUrl = (next: TFilter) => {
    const nextQuery = serializeAll(schema, next, route.query)
    if (!queryEqual(nextQuery, route.query)) {
      void router[mode in router ? mode : 'push']({ query: nextQuery })
    }
  }

  watch(
    () => filter.value,
    (next) => {
      const changed = changedKeys(next, previous)
      previous = snapshot(next)
      if (isSyncingFromQuery) {
        return
      }
      const shouldResetPage = hasPageField && changed.some((key) => resetPageOn.has(key))
      if (shouldResetPage && (next as Record<string, unknown>).page !== 1) {
        ;(filter.value as Record<string, unknown>).page = 1
        return
      }
      writeToUrl(next)
    },
    { deep: true },
  )

  watch(
    () => route.query,
    (query) => {
      const parsed = parseAll(schema, query)
      const changed = (Object.keys(parsed) as (keyof TFilter)[]).filter(
        (key) => !shallowEqualValue(parsed[key], filter.value[key]),
      )
      if (changed.length === 0) {
        return
      }
      isSyncingFromQuery = true
      const next = { ...filter.value } as Record<string, unknown>
      for (const key of changed) {
        next[key as string] = parsed[key]
      }
      filter.value = next as TFilter
      // Defer the flag reset until after the state-watcher has settled.
      queueMicrotask(() => {
        isSyncingFromQuery = false
      })
    },
  )

  return {
    filter,
    reset: () => {
      const cleared = options.cleared
      filter.value = cleared ? ({ ...emptyFilter(schema), ...cleared } as TFilter) : { ...defaults }
    },
  }
}
